// historial-cambio-dialog.component.ts
import { Component, OnInit, ViewChild, Inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { ChartConfiguration, ChartType } from 'chart.js';
import {
  HistorialCambioDto,
  HistorialCambioFilter,
  HistorialCambioEstadisticas
} from '@models/historial-cambio/historial-cambio.models';
import { HistorialCambioService } from '@services/historial-cambio/historial-cambio.service';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';

export interface HistorialCambioDialogData {
  idEntidad?: number;
  nombreEntidad?: string;
}

@Component({
  selector: 'app-historial-cambio-dialog',
  templateUrl: 'historial-cambio-dialog.component.html',
  styleUrls: ['./historial-cambio-dialog.component.css']
})
export class HistorialCambioDialogComponent implements OnInit {

  filterForm!: FormGroup;
  selectedTab = 0;
  datosCargados = false;
  idEntidad?: number;
  nombreEntidad?: string;
  periodoSeleccionado: 'dia' | 'semana' | 'mes' = 'mes';

  // Data
  historialCambios: HistorialCambioDto[] = [];
  estadisticas?: HistorialCambioEstadisticas;
  dataSource = new MatTableDataSource<HistorialCambioDto>();

  displayedColumns: string[] = [
    'fechaCambio',
    'nombreEntidad',
    'nombrePropiedad',
    'valorAntiguo',
    'valorNuevo',
    'grupoCambio'
  ];

  // Configuración de gráficos
  lineChartData: any = { datasets: [] };
  lineChartLabels: string[] = [];
  lineChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: any) => `Cambios: ${context.raw}`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      } as any
    }
  };

  barChartEntidades: any = { datasets: [] };
  barChartEntidadesLabels: string[] = [];

  barChartPropiedades: any = { datasets: [] };
  barChartPropiedadesLabels: string[] = [];

  pieChartGrupos: any = { datasets: [] };
  pieChartGruposLabels: string[] = [];

  barChartHoras: any = { datasets: [] };
  barChartHorasLabels: string[] = [];

  barChartDias: any = { datasets: [] };
  barChartDiasLabels: string[] = [];

  barChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: { display: false }
    },
    scales: {
      x: {
        beginAtZero: true
      }
    }
  };

  barChartOptionsHoras: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      } as any,
      x: {
        ticks: {
          callback: (value: any) => `${value}:00`
        }
      } as any
    }
  };

  pieChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const label = context.label || '';
            const value = context.raw || 0;
            return `${label}: ${value} (${((value / this.estadisticas!.totalCambios) * 100).toFixed(1)}%)`;
          }
        }
      }
    }
  };

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private fb: FormBuilder,
    private historialService: HistorialCambioService,
    private spinner: NgxSpinnerService,
    private snackBar: SnackBarService,
    public dialogRef: MatDialogRef<HistorialCambioDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: HistorialCambioDialogData
  ) {
    this.idEntidad = data?.idEntidad;
    this.nombreEntidad = data?.nombreEntidad;
  }

  ngOnInit(): void {
    this.createForm();

    // Si viene con entidad específica, cargar automáticamente
    if (this.idEntidad && this.nombreEntidad) {
      this.aplicarFiltros();
    }
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  private createForm(): void {
    this.filterForm = this.fb.group({
      nombrePropiedad: [null],
      grupoCambio: [null],
      fechaCambioDesde: [null],
      fechaCambioHasta: [null]
    });
  }

  aplicarFiltros(): void {
    this.spinner.show('spHistorialCambios');

    const filter: HistorialCambioFilter = {
      idEntidad: this.idEntidad,
      nombreEntidad: this.nombreEntidad,
      nombrePropiedad: this.filterForm.get('nombrePropiedad')?.value,
      grupoCambio: this.filterForm.get('grupoCambio')?.value,
      fechaCambioDesde: this.filterForm.get('fechaCambioDesde')?.value,
      fechaCambioHasta: this.filterForm.get('fechaCambioHasta')?.value
    };

    // Cargar listado y estadísticas en paralelo
    Promise.all([
      this.historialService.getAllHistorialCambioByFilter(filter).toPromise(),
      this.historialService.getEstadisticasHistorialCambio(filter).toPromise()
    ]).then(([cambios, stats]) => {
      this.historialCambios = cambios || [];
      this.estadisticas = stats;
      this.dataSource.data = this.historialCambios;
      this.datosCargados = true;

      if (this.estadisticas) {
        this.prepararGraficos();
      }

      this.spinner.hide('spHistorialCambios');

      if (this.historialCambios.length === 0) {
        this.snackBar.showInfo('No se encontraron cambios con los filtros aplicados', 'Info');
      }
    }).catch(error => {
      this.spinner.hide('spHistorialCambios');
      this.snackBar.showError(error, 'Error al cargar historial');
    });
  }

  limpiarFiltros(): void {
    this.filterForm.reset();
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  cambiarPeriodo(periodo: 'dia' | 'semana' | 'mes'): void {
    this.periodoSeleccionado = periodo;
    this.actualizarGraficoTemporal();
  }

  private prepararGraficos(): void {
    if (!this.estadisticas) return;

    this.actualizarGraficoTemporal();
    this.prepararGraficoEntidades();
    this.prepararGraficoPropiedades();
    this.prepararGraficoGrupos();
    this.prepararGraficoHoras();
    this.prepararGraficoDias();
  }

  private actualizarGraficoTemporal(): void {
    if (!this.estadisticas) return;

    let datos: any[] = [];

    switch (this.periodoSeleccionado) {
      case 'dia':
        datos = this.estadisticas.cambiosPorDia || []; // Agregar || []
        break;
      case 'semana':
        datos = this.estadisticas.cambiosPorSemana || []; // Agregar || []
        break;
      case 'mes':
        datos = this.estadisticas.cambiosPorMes || []; // Agregar || []
        break;
    }

    this.lineChartLabels = datos.map(d => d.label);
    this.lineChartData = {
      datasets: [{
        data: datos.map(d => d.cantidad),
        label: 'Cambios',
        borderColor: '#009688',
        backgroundColor: 'rgba(0, 150, 136, 0.1)',
        fill: true,
        tension: 0.4
      }]
    };
  }

  private prepararGraficoEntidades(): void {
    if (!this.estadisticas?.topEntidadesModificadas) return; // Validar

    this.barChartEntidadesLabels = this.estadisticas.topEntidadesModificadas.map(e => e.nombre);
    this.barChartEntidades = {
      datasets: [{
        data: this.estadisticas.topEntidadesModificadas.map(e => e.cantidad),
        backgroundColor: '#2196F3',
        borderColor: '#1976D2',
        borderWidth: 1
      }]
    };
  }

  private prepararGraficoPropiedades(): void {
    if (!this.estadisticas?.topPropiedadesModificadas) return; // Validar

    this.barChartPropiedadesLabels = this.estadisticas.topPropiedadesModificadas.map(p => p.nombre);
    this.barChartPropiedades = {
      datasets: [{
        data: this.estadisticas.topPropiedadesModificadas.map(p => p.cantidad),
        backgroundColor: '#FF9800',
        borderColor: '#F57C00',
        borderWidth: 1
      }]
    };
  }

  private prepararGraficoGrupos(): void {
    if (!this.estadisticas?.distribucionPorGrupo?.length) return; // Validar

    this.pieChartGruposLabels = this.estadisticas.distribucionPorGrupo.map(g => g.grupoCambio);

    const colores = [
      '#4CAF50', '#2196F3', '#FF9800', '#9C27B0',
      '#F44336', '#00BCD4', '#FFEB3B', '#795548'
    ];

    this.pieChartGrupos = {
      datasets: [{
        data: this.estadisticas.distribucionPorGrupo.map(g => g.cantidad),
        backgroundColor: colores,
        borderWidth: 2,
        borderColor: '#fff'
      }]
    };
  }

  private prepararGraficoHoras(): void {
    if (!this.estadisticas?.heatmapPorHora) return; // Validar

    const horasCantidades = new Array(24).fill(0);
    this.estadisticas.heatmapPorHora.forEach(h => {
      horasCantidades[h.hora] = h.cantidad;
    });

    this.barChartHorasLabels = Array.from({ length: 24 }, (_, i) => i.toString());
    this.barChartHoras = {
      datasets: [{
        data: horasCantidades,
        backgroundColor: horasCantidades.map((cant, hora) => {
          return (hora < 6 || hora >= 20) ? '#f44336' : '#009688';
        }),
        borderWidth: 1
      }]
    };
  }

  private prepararGraficoDias(): void {
    if (!this.estadisticas?.heatmapPorDiaSemana) return; // Validar

    this.barChartDiasLabels = this.estadisticas.heatmapPorDiaSemana.map(d => d.nombreDia);
    this.barChartDias = {
      datasets: [{
        data: this.estadisticas.heatmapPorDiaSemana.map(d => d.cantidad),
        backgroundColor: '#673AB7',
        borderColor: '#512DA8',
        borderWidth: 1
      }]
    };
  }
}
